import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Spacer from '~/components/Spacer/Spacer';
import classes from './UGC.module.scss';
import useGetBuildingDetails from '~/hooks/building/useGetBuildingDetails';
import { useHistory, useParams } from 'react-router-dom';
import { Building } from '~/types/building';
import CobuTable from '~/components/CobuTable/CobuTable';
import { useForm } from 'react-hook-form';
import { IKeyWithPlaceholder, ITableData, IUGCForm } from './interfaces';
import { jsSnippetFormField, otherFormFields, TableKeys } from './constants';
import { FormField } from '~/components/FormField';
import { FormInputConfig } from '~/types/residentManagement';
import { CellProps } from 'react-table';
import useUGC from './hooks';
import Spinner from '~/components/Spinner/Spinner';
import { IUGCDefault, IUGCSetting } from '~/api/userGeneratedContent';
import { Colors } from '~/enums/Colors';
import { toast } from 'react-toastify';
import { pick } from 'lodash';
import GenericModal from '~/components/GenericModal/GenericModal';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

const env = process.env.NODE_ENV;

const domain = {
  production: 'api.doorbell.me',
  development: 'staging.doorbell.me',
  test: 'localhost:1337'
};

export const UGC = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const buildingDetails: null | Partial<Building> = useGetBuildingDetails(
    buildingId,
    true
  );

  const history = useHistory();

  const defaultJsSnippet = `<script>
      window.highlightcolor = "#EBAD39";
      window.buttoncolor = "#EBAD39";
      window.accentcolor = "#7F7F7F";
      window.building_id = "${buildingId}";
  </script>
  <script src="https://${domain[env]}/js/mycommunity.js" defer></script>
  <div id="cobu-community"></div>`;

  const { isLoading, ugcTableData, ugcSetting, onSubmit } = useUGC(buildingId);

  const [noteModal, setNoteModal] = useState<{ noteKey: string; label: string } | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [viewNote, setViewNote] = useState<{ label: string; text: string } | null>(null);

  const {
    register,
    handleSubmit,
    errors,
    formState: { isDirty, dirtyFields },
    control,
    reset,
    setValue
  } = useForm<IUGCForm>({
    defaultValues: {
      chat_on: true,
      marketplace_on: true,
      events_on: true,
      groups_on: true,
      reviews_on: true,
      video_on: true,
      chat_past_days: 90,
      marketplace_past_days: 90,
      events_past_days: 90,
      reviews_past_days: 90,
      events_future_days: 90,
      js_snippet: defaultJsSnippet,
      notes_chat: '',
      notes_marketplace: '',
      notes_events: '',
      notes_groups: '',
      notes_reviews: '',
    }
  });

  const getJsSnippet = (ugcSetting: IUGCSetting) => {
    // if js_snippet is not empty, return it
    if (ugcSetting.js_snippet && ugcSetting.js_snippet.length > 0) {
      return ugcSetting.js_snippet;
    }
    // if js_snippet is empty, return defaultJsSnippet
    return defaultJsSnippet;
  };

  useEffect(() => {
    if (ugcSetting) {
      reset({ ...ugcSetting, js_snippet: getJsSnippet(ugcSetting) });
    }
  }, [ugcSetting]);

  const cacheError = useRef(errors);

  cacheError.current = errors;

  const generateField = useCallback((fieldConfig: FormInputConfig) => {
    return (
      <div
        key={fieldConfig.id}
        style={fieldConfig.displayNone ? { display: 'none' } : {}}
      >
        <div className='item'>
          {!!fieldConfig?.label && (
            <label
              style={{ color: Colors.cobuDarkBlue }}
              className={`${fieldConfig?.styles?.lableClassName} sub-heading semi-bold`}
              htmlFor='title'
            >
              {fieldConfig.label}
            </label>
          )}
          <FormField
            fieldConfig={fieldConfig}
            errors={cacheError.current}
            register={register}
            control={control}
          />
        </div>
      </div>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'SECTIONS',
        accessor: 'source',
        Cell: (cell: CellProps<ITableData>) => {
          if (!cell.value) {
            return null;
          }

          const { key, placeholder } = cell.value;

          return (
            <div key={key} className={classes.sourceContainer}>
              {generateField({
                id: key,
                inputType: 'switch',
                isRequired: false
              })}
              {placeholder}
            </div>
          );
        }
      },
      {
        Header: 'DAYS PAST',
        accessor: TableKeys.DaysInPast,
        Cell: (cell: CellProps<ITableData>) => {
          if (!cell.value) {
            return null;
          }

          const { key } = cell.value;

          //@ts-ignore
          const isRequired = !!dirtyFields[cell.row.original.source.key];

          return <div className={classes.daysInput}>{generateField({ id: key, inputType: 'text', isRequired: isRequired })}</div>;
        }
      },
      {
        Header: 'DAYS FUTURE',
        accessor: TableKeys.DaysInFuture,
        Cell: (cell: CellProps<ITableData>) => {
          if (!cell.value) {
            return null;
          }

          const { key } = cell.value;

          //@ts-ignore
          const isRequired = !!dirtyFields[cell.row.original.source.key];

          return <div className={classes.daysInput}>{generateField({ id: key, inputType: 'text', isRequired: isRequired })}</div>;
        }
      },
      {
        Header: 'PAGE SNIPPET',
        accessor: TableKeys.PageSnippet,
        Cell: (cell: CellProps<ITableData>) => {
          if (!cell.value) {
            return null;
          }

          const { key }: IKeyWithPlaceholder = cell.value;

          return generateField({
            id: key,
            inputType: 'textarea',
            isRequired: false,
            inputTypeProps: { rows: 2, cols: 40 }
          });

        }
      },
      {
        Header: 'NOTE',
        accessor: TableKeys.Notes,
        Cell: (cell: CellProps<ITableData>) => {
          if (!cell.value) {
            return null;
          }

          const { key, placeholder }: IKeyWithPlaceholder = cell.value;
          const currentNote: string = (ugcSetting?.[key as keyof IUGCSetting] as string) || '';
          const sectionLabel = cell.row.original[TableKeys.Source]?.placeholder || '';

          return (
            <div className={classes.noteCell}>
              {currentNote && (
                <span className={classes.notePreview}>{currentNote}</span>
              )}
              <div className={classes.noteActions}>
                {currentNote && (
                  <button
                    type='button'
                    className={classes.penButton}
                    title='View note'
                    onClick={() => setViewNote({ label: sectionLabel, text: currentNote })}
                  >
                    <VisibilityIcon fontSize='small' />
                  </button>
                )}
                <button
                  type='button'
                  className={classes.penButton}
                  title={currentNote ? 'Edit note' : 'Add note'}
                  onClick={() => {
                    setNoteDraft(currentNote);
                    setNoteModal({ noteKey: key, label: sectionLabel });
                  }}
                >
                  <EditIcon fontSize='small' />
                </button>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'ACTIONS',
        accessor: '',
        Cell: (cell: CellProps<ITableData>) => {
          const {
            [TableKeys.Source]: { key },
            daysInPast
          } = cell.row.original;

          const dayInPastKey = daysInPast?.key || '';
          const canManageContent =
            key.includes('chat') ||
            key.includes('marketplace') ||
            key.includes('reviews') ||
            key.includes('video') ||
            key.includes('events') ||
            key.includes('groups');

          if (!canManageContent) {
            return null;
          }

          const isMarketplace = key.includes('marketplace');
          const isVideo = key.includes('video');
          const isReviews = key.includes('reviews');
          const isEvents = key.includes('events');
          const isGroups = key.includes('groups');
          return (
            <div
              className={`btn-standard btn-secondary underline-text cursor-pointer ${classes.textButton}`}
              onClick={() => {
                if (isMarketplace) {
                  if (!ugcSetting?.marketplaceGroupId) {
                    toast('Group doesnt exist!');

                    return;
                  }
                }

                const filledData =
                  //@ts-ignore
                  ugcSetting && dayInPastKey ? ugcSetting[dayInPastKey] : '';

                const queryParams = new URLSearchParams({
                  ...(isMarketplace && ugcSetting?.marketplaceGroupId
                    ? { groupId: ugcSetting?.marketplaceGroupId }
                    : {}),
                  ...(filledData ? { days: filledData } : {}),
                  ...(dayInPastKey ? { id: dayInPastKey } : {}),
                  ...(isReviews ? { type: 'reviews' } : {}),
                  ...(isVideo ? { type: 'video' } : {}),
                  ...(isEvents ? { type: 'events' } : {}),
                  ...(isGroups ? { type: 'groups' } : {})
                }).toString();

                history.push(
                  `/building/${buildingId}/UGC/${ugcSetting?.uuid}?${queryParams}`
                );
              }}
            >
              Manage Visible Content
            </div>
          );
        }
      }
    ],
    [ugcSetting, dirtyFields]
  );

  return (
    <div className={`${classes.container} float-spinner-parent`}>
      {isLoading && <Spinner float />}
      <Breadcrumbs />
      <Spacer height={20} />
      {buildingDetails && (
        <h2>{(buildingDetails as Partial<Building>).name}</h2>
      )}
      <form
        onSubmit={handleSubmit((formData) => {
          if (!isDirty) {
            toast('Please edit the form');
            return;
          }

          const { uuid, shortcode, ...rest } = formData as IUGCSetting;

          //@ts-ignore
          const pickedData: IUGCDefault = pick(rest, Object.keys(dirtyFields));

          onSubmit({ uuid, ...pickedData });
        })}
      >
        <div className={classes.tableTopRow}>
          <button
            className='btn-standard btn-primary'
            type='button'
            onClick={() => history.push(`/building/${buildingId}/UGC/ai-content`)}
          >
            Manage Content with AI
          </button>
        </div>
        <CobuTable
          ignoreDefaultSortBehaviour
          columns={columns}
          data={ugcTableData ?? []}
        />
        <Spacer height={100} />
        <div className={classes.grid}>
          <div className={classes.grid}>
            {otherFormFields.map(generateField)}
          </div>
          <div>{generateField(jsSnippetFormField)}</div>
        </div>
        <button
          className='btn-standard btn-primary margin-left-16 '
          type='submit'
          style={{ alignSelf: 'flex-end' }}
        >
          {ugcSetting ? 'Save' : 'Create'}
        </button>
      </form>
      {viewNote && (
        <GenericModal
          showModal={!!viewNote}
          title={`Note — ${viewNote.label}`}
          hideActionButton
          content={<p className={classes.noteViewContent}>{viewNote.text}</p>}
          onChange={(open) => { if (!open) setViewNote(null); }}
        />
      )}
      {noteModal && (
        <GenericModal
          showModal={!!noteModal}
          title={`Note — ${noteModal.label}`}
          actionText='Save'
          actionButtonsInHeader
          content={
            <textarea
              className={classes.noteModalTextarea}
              value={noteDraft}
              placeholder='Type your internal note here...'
              rows={5}
              onChange={(e) => setNoteDraft(e.target.value)}
            />
          }
          onChange={(open) => {
            if (!open) setNoteModal(null);
          }}
          actionFunction={async () => {
            await onSubmit({ uuid: ugcSetting?.uuid, [noteModal.noteKey]: noteDraft } as unknown as IUGCSetting);
            setValue(noteModal.noteKey as keyof IUGCForm, noteDraft);
            setNoteModal(null);
          }}
        />
      )}
    </div>
  );
};
